<template>
  <div class="">
    <!-- Backdrop -->
    <div v-if="open" class="  z-40" @click="close" />

    <!-- Bell FAB -->
    <div class="relative flex flex-row items-center">
      <button @click="toggle"
        class="mr-0 w-8 h-8 bg-primary rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all duration-200 text-white hover:text-primary">
        <font-awesome-icon icon="bell" class="" />
      </button>
      <span v-if="unreadCount > 0"
        class="z-100 absolute top-1 left-[32px] w-[22px] h-[22px] p-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
        {{ unreadCount > 99 ? "99+" : unreadCount }}
      </span>
    </div>
    <!-- Dropdown Panel -->
    <div v-if="open"
      class="fixed top-4 left-4 z-50 w-[290px] max-h-96 bg-white rounded-lg shadow-xl border border-gray-600  flex flex-col">
      <div class="px-3 py-2 border-b border-gray-100 text-sm font-medium text-gray-500 flex flex-row items-center ">
        <span class="flex-grow">Notifications</span>
        <button @click="viewAll" class="mr-3 text-xs text-primary hover:underline">
          View all
        </button>
        <button @click="close" class=" text-gray-300 hover:text-gray-500 transition-colors p-0 w-4 h-4">
          <font-awesome-icon icon="times" class="text-xs text-red" />
        </button>
      </div> 

      <div class=" flex-1 overflow-y-auto">
        <div v-for="n in notifications" :key="n.key"
          class="flex items-start gap-2 px-3 py-2 border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
          @click="navigate(n)">
          <!-- Type badge -->
          <span :class="typeClass(n.type)"
            class="mt-0.5 shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded">
            {{ typeLabel(n.type) }}
          </span>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-800 truncate">{{ n.title || n.sourcePath }}</p>
            <p class="text-xs text-gray-600 ">{{ n.body || n.eventType }}</p>
            <p class="text-[10px] text-gray-400 mt-0.5">{{ formatTime(n.createdAt) }}</p>
          </div>

          <!-- Delete -->
          <button @click.stop="deleteNotification(n.key)"
            class="shrink-0 mt-0.5 p-1 text-gray-300 hover:text-red-500 transition-colors">
            <font-awesome-icon icon="trash" class="text-xs" />
          </button>
        </div>

        <p v-if="notifications.length === 0" class="px-3 py-6 text-center text-sm text-gray-400">
          No notifications
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

const store = useStore();
const router = useRouter();
import { auth, db } from "../db.js";

const open = ref(false);

const notifications = computed(() => {
  const raw = store.state.notifications;
  if (!raw || typeof raw !== "object") return [];
  return Object.entries(raw)
    .map(([key, val]) => ({ key, ...val }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
});

const unreadCount = computed(() => notifications.value.filter((n) => n.read !== true).length);

function getNotificationsPath() {
  const userId = auth.currentUser?.uid;
  return userId ? `Notifications/${userId}` : null;
}

function toggle() {
  open.value = !open.value;
  if (open.value) {
    markAllRead();
  }
}

function close() {
  open.value = false;
}

function viewAll() {
  close();
  router.push("/Home/Notifications");
}

function markAllRead() {
  const notificationsPath = getNotificationsPath();
  if (!notificationsPath) return;

  const unread = notifications.value.filter((n) => n.read !== true);
  for (const n of unread) {
    db.update(`${notificationsPath}/${n.key}`, { read: true });
  }
}

function deleteNotification(key) {
  const notificationsPath = getNotificationsPath();
  if (notificationsPath) db.delete(`${notificationsPath}/${key}`);
}

function navigate(notification) {
  const destination = notification.route || (notification.sourcePath ? `/${notification.sourcePath}` : null);
  if (!destination) return;
  close();
  router.push(destination);
}

function typeLabel(type) {
  const t = (type || "").toLowerCase();
  if (t === "warn" || t === "warning") return "Warn";
  if (t === "error") return "Error";
  return "Info";
}

function typeClass(type) {
  const t = (type || "").toLowerCase();
  if (t === "warn" || t === "warning") return "bg-orange-100 text-orange-700";
  if (t === "error") return "bg-red-100 text-red-700";
  return "bg-blue-100 text-blue-700";
}

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString();
}
</script>
